import * as Location from 'expo-location';
import { Platform } from 'react-native';

export interface TimezoneInfo {
  timezone: string;
  offset: string;
  granted: boolean;
}

/**
 * Service for detecting and managing user timezone
 */
export class TimezoneService {
  
  /**
   * Get device timezone with permission handling
   * Returns: { timezone, offset, granted }
   */
  static async getDeviceTimezone(): Promise<TimezoneInfo> {
    try {
      console.log('[TimezoneService] Getting device timezone...');
      
      // Platform-specific timezone detection
      if (Platform.OS === 'web') {
        return this.getWebTimezone();
      } else {
        return await this.getNativeTimezone();
      }
    } catch (error) {
      console.error('[TimezoneService] Error getting timezone:', error);
      return {
        timezone: 'UTC',
        offset: '+00:00',
        granted: false
      };
    }
  }
  
  /**
   * Web: Get timezone from browser (no permission needed)
   */
  private static getWebTimezone(): TimezoneInfo {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const offset = new Date().toString().match(/GMT([+-]\d{4})/)?.[1] || '+0000';
      const formattedOffset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
      
      console.log('[TimezoneService] Web timezone:', { timezone, offset: formattedOffset });
      
      return {
        timezone,
        offset: formattedOffset,
        granted: true // Web doesn't need permission
      };
    } catch (error) {
      console.error('[TimezoneService] Error getting web timezone:', error);
      return {
        timezone: 'UTC',
        offset: '+00:00',
        granted: true
      };
    }
  }
  
  /**
   * iOS/Android: Get timezone with location permission
   * Note: We only need permission for expo-location API, 
   * but Intl.DateTimeFormat works without it
   */
  private static async getNativeTimezone(): Promise<TimezoneInfo> {
    try {
      // First, try to get timezone without location permission
      // Intl API works without permission on most devices
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const offset = new Date().toString().match(/GMT([+-]\d{4})/)?.[1] || '+0000';
      const formattedOffset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
      
      console.log('[TimezoneService] Native timezone (no permission needed):', { timezone, offset: formattedOffset });
      
      return {
        timezone,
        offset: formattedOffset,
        granted: true // We got it without needing location permission
      };
      
    } catch (error) {
      console.error('[TimezoneService] Error with Intl API, trying with location permission:', error);
      
      // Fallback: Try with location permission
      try {
        const { status: currentStatus } = await Location.getForegroundPermissionsAsync();
        let permissionGranted = currentStatus === 'granted';
        
        if (!permissionGranted) {
          console.log('[TimezoneService] Requesting location permission...');
          const { status: newStatus } = await Location.requestForegroundPermissionsAsync();
          permissionGranted = newStatus === 'granted';
        }
        
        if (permissionGranted) {
          // Try Intl API again
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const offset = new Date().toString().match(/GMT([+-]\d{4})/)?.[1] || '+0000';
          const formattedOffset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
          
          return {
            timezone,
            offset: formattedOffset,
            granted: true
          };
        }
      } catch (permError) {
        console.error('[TimezoneService] Permission error:', permError);
      }
      
      // All methods failed - return UTC
      return {
        timezone: 'UTC',
        offset: '+00:00',
        granted: false
      };
    }
  }
  
  /**
   * Compare two timezones (case-insensitive)
   */
  static areTimezonesEqual(tz1?: string, tz2?: string): boolean {
    if (!tz1 || !tz2) return false;
    return tz1.toLowerCase() === tz2.toLowerCase();
  }
  
  /**
   * Get timezone display name
   */
  static getTimezoneDisplayName(timezone: string): string {
    try {
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        timeZoneName: 'long'
      });
      const parts = formatter.formatToParts(new Date());
      const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value;
      return timeZoneName || timezone;
    } catch (error) {
      return timezone;
    }
  }
}

